// src/pages/staff/components/StaffDetailModal.tsx

import React from 'react';
import {
  Modal,
  Descriptions,
  Typography,
  Space,
  Avatar,
  Tag,
  Badge,
  Button,
  Divider,
  Card,
  Row,
  Col,
  List,
  Alert,
} from 'antd';
import {
  UserOutlined,
  EditOutlined,
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  BankOutlined,
  TeamOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import type { StaffMember } from '../../../types/api.types';
import { STAFF_ROLE_OPTIONS } from '../../../types/api.types';

const { Text } = Typography;

interface StaffDetailModalProps {
  open: boolean;
  onClose: () => void;
  staff: StaffMember | null;
  onEdit?: () => void;
  canEdit?: boolean;
}

const StaffDetailModal: React.FC<StaffDetailModalProps> = ({
  open,
  onClose,
  staff,
  onEdit,
  canEdit = false,
}) => {
  if (!staff) return null;

  const roleConfig = STAFF_ROLE_OPTIONS.find(r => r.value === staff.role) || {
    label: staff.role,
    color: 'default',
    description: 'Rôle personnalisé'
  };

  const formatPermissions = (permissions: any) => {
    const permissionGroups = {
      'Membres': [
        { key: 'members.read', label: 'Consultation' },
        { key: 'members.create', label: 'Création' },
        { key: 'members.update', label: 'Modification' },
        { key: 'members.delete', label: 'Suppression' },
      ],
      'Épargnes': [
        { key: 'savings.read', label: 'Consultation' },
        { key: 'savings.create', label: 'Création' },
        { key: 'savings.update', label: 'Modification' },
      ],
      'Crédits': [
        { key: 'credits.read', label: 'Consultation' },
        { key: 'credits.create', label: 'Création' },
        { key: 'credits.update', label: 'Modification' },
        { key: 'credits.approve', label: 'Approbation' },
        { key: 'credits.reject', label: 'Rejet' },
      ],
      'Rapports': [
        { key: 'reports.read', label: 'Consultation' },
        { key: 'reports.generate', label: 'Génération' },
      ],
      'Comptes': [
        { key: 'accounts.read', label: 'Consultation' },
        { key: 'accounts.update', label: 'Modification' },
      ],
      'Personnel': [
        { key: 'staff.read', label: 'Consultation' },
        { key: 'staff.create', label: 'Création' },
        { key: 'staff.update', label: 'Modification' },
      ],
      'Paramètres': [
        { key: 'settings.read', label: 'Consultation' },
        { key: 'settings.update', label: 'Modification' },
      ],
    };

    return Object.entries(permissionGroups).map(([group, perms]) => {
      const groupPermissions = perms.filter(p => permissions[p.key]);
      if (groupPermissions.length === 0) return null;

      return (
        <Card
          key={group}
          size="small"
          title={group}
          style={{ marginBottom: '8px' }}
        >
          <Space wrap>
            {groupPermissions.map(perm => (
              <Tag key={perm.key} color="green" icon={<CheckCircleOutlined />}>
                {perm.label}
              </Tag>
            ))}
          </Space>
        </Card>
      );
    }).filter(Boolean);
  };

  const approvalLimits = staff.permissions.approvalLimits;

  return (
    <Modal
      title={
        <Space>
          <Avatar
            size={40}
            icon={<UserOutlined />}
            style={{
              backgroundColor: staff.isActive ? '#52c41a' : '#d9d9d9',
              color: 'white',
            }}
          >
            {`${staff.User.firstName[0]}${staff.User.lastName[0]}`}
          </Avatar>
          <div>
            <div style={{ fontSize: '18px', fontWeight: 600 }}>
              {`${staff.User.firstName} ${staff.User.lastName}`}
            </div>
            <div style={{ fontSize: '14px', color: '#666' }}>
              {staff.staffNumber}
            </div>
          </div>
        </Space>
      }
      open={open}
      onCancel={onClose}
      width={900}
      footer={[
        <Button key="close" onClick={onClose}>
          Fermer
        </Button>,
        ...(canEdit && onEdit ? [
          <Button key="edit" type="primary" icon={<EditOutlined />} onClick={onEdit}>
            Modifier
          </Button>
        ] : []),
      ]}
      styles={{
        body: { padding: '24px', maxHeight: '70vh', overflowY: 'auto' },
      }}
    >
      {/* Statut et informations de base */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Alert
            message={
              <Space>
                <Badge 
                  status={staff.isActive ? 'success' : 'error'} 
                  text={staff.isActive ? 'Personnel actif' : 'Personnel inactif'}
                />
                <Divider type="vertical" />
                <Tag color={roleConfig.color}>{roleConfig.label}</Tag>
              </Space>
            }
            type={staff.isActive ? 'success' : 'warning'}
            showIcon
          />
        </Col>
      </Row>

      {/* Informations personnelles */}
      <Card title="Informations personnelles" style={{ marginBottom: '16px' }}>
        <Descriptions column={2} size="small">
          <Descriptions.Item 
            label={<Space><MailOutlined /> Email</Space>}
          >
            {staff.User.email}
          </Descriptions.Item>
          <Descriptions.Item 
            label={<Space><PhoneOutlined /> Téléphone</Space>}
          >
            {staff.User.phone || 'Non renseigné'}
          </Descriptions.Item>
          <Descriptions.Item 
            label={<Space><UserOutlined /> Type d'utilisateur</Space>}
          >
            <Tag>{staff.User.userType}</Tag>
          </Descriptions.Item>
          <Descriptions.Item 
            label={<Space><CheckCircleOutlined /> Email vérifié</Space>}
          >
            <Badge 
              status={staff.User.emailVerified ? 'success' : 'error'} 
              text={staff.User.emailVerified ? 'Vérifié' : 'Non vérifié'}
            />
          </Descriptions.Item>
          <Descriptions.Item 
            label={<Space><CalendarOutlined /> Dernière connexion</Space>}
          >
            {staff.User.lastLoginAt 
              ? new Date(staff.User.lastLoginAt).toLocaleString('fr-FR')
              : 'Jamais connecté'
            }
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Informations professionnelles */}
      <Card title="Informations professionnelles" style={{ marginBottom: '16px' }}>
        <Descriptions column={2} size="small">
          <Descriptions.Item 
            label={<Space><TeamOutlined /> Rôle</Space>}
          >
            <Tag color={roleConfig.color}>{roleConfig.label}</Tag>
          </Descriptions.Item>
          <Descriptions.Item 
            label={<Space><BankOutlined /> Département</Space>}
          >
            {staff.department || 'Non renseigné'}
          </Descriptions.Item>
          <Descriptions.Item 
            label={<Space><UserOutlined /> Poste</Space>}
          >
            {staff.position || 'Non renseigné'}
          </Descriptions.Item>
          <Descriptions.Item 
            label={<Space><CalendarOutlined /> Date d'embauche</Space>}
          >
            {new Date(staff.hireDate).toLocaleDateString('fr-FR')}
          </Descriptions.Item>
          <Descriptions.Item 
            label={<Space><BankOutlined /> Salaire</Space>}
          >
            {staff.salary 
              ? `${staff.salary.toLocaleString()} CFA`
              : 'Non renseigné'
            }
          </Descriptions.Item>
          <Descriptions.Item 
            label={<Space><UserOutlined /> Superviseur</Space>}
          >
            {staff.Supervisor 
              ? `${staff.Supervisor.User.firstName} ${staff.Supervisor.User.lastName}`
              : 'Aucun'
            }
          </Descriptions.Item>
        </Descriptions>

        {staff.notes && (
          <>
            <Divider orientation="left" style={{ margin: '16px 0' }}>Notes</Divider>
            <Text style={{ fontStyle: 'italic', color: '#666' }}>
              {staff.notes}
            </Text>
          </>
        )}
      </Card>

      {/* Limites d'approbation */}
      <Card title="Limites d'approbation" style={{ marginBottom: '16px' }}>
        <Row gutter={[16, 8]}>
          {Object.entries(approvalLimits).map(([key, value]) => (
            <Col span={8} key={key}>
              <div style={{ textAlign: 'center', padding: '12px', background: '#f5f5f5', borderRadius: '8px' }}>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                  {key === 'credit' ? 'Crédit' : 
                   key === 'savings' ? 'Épargne' : 
                   key === 'solidarity' ? 'Solidarité' : 
                   key === 'repayment' ? 'Remboursement' : key}
                </div>
                <div style={{ fontSize: '16px', fontWeight: 600, color: '#1890ff' }}>
                  {value === 'unlimited' ? 'Illimité' : 
                   typeof value === 'number' ? `${value.toLocaleString()} CFA` : 
                   value || 'Non défini'}
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </Card>

      {/* Permissions */}
      <Card title="Permissions" style={{ marginBottom: '16px' }}>
        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
          {formatPermissions(staff.permissions)}
        </div>
      </Card>

      {/* Subordinates */}
      {staff.Subordinates && staff.Subordinates.length > 0 && (
        <Card title="Équipe supervisée" style={{ marginBottom: '16px' }}>
          <List
            size="small"
            dataSource={staff.Subordinates}
            renderItem={(subordinate) => (
              <List.Item>
                <Space>
                  <Avatar size="small" icon={<UserOutlined />}>
                    {`${subordinate.User.firstName[0]}${subordinate.User.lastName[0]}`}
                  </Avatar>
                  <span>{`${subordinate.User.firstName} ${subordinate.User.lastName}`}</span>
                  <Tag>{subordinate.role}</Tag>
                </Space>
              </List.Item>
            )}
          />
        </Card>
      )}

      {/* Informations système */}
      <Card title="Informations système" size="small">
        <Descriptions column={2} size="small">
          <Descriptions.Item label="Créé le">
            {new Date(staff.createdAt).toLocaleString('fr-FR')}
          </Descriptions.Item>
          <Descriptions.Item label="Modifié le">
            {new Date(staff.updatedAt).toLocaleString('fr-FR')}
          </Descriptions.Item>
          <Descriptions.Item label="Tentatives de connexion">
            {staff.User.loginAttempts}
          </Descriptions.Item>
          <Descriptions.Item label="Compte bloqué jusqu'à">
            {staff.User.lockedUntil 
              ? new Date(staff.User.lockedUntil).toLocaleString('fr-FR')
              : 'Non bloqué'
            }
          </Descriptions.Item>
        </Descriptions>
      </Card>
    </Modal>
  );
};

export default StaffDetailModal;